import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { storiesApi } from '../api/client';
import StoryCard from '../components/StoryCard';
import SEO, { SITE_URL } from '../components/SEO';

const TYPES = [
  { value: '', label: 'All stories' },
  { value: 'personal', label: '✍️ Personal' },
  { value: 'investigation', label: '🔍 Investigation' },
  { value: 'talent', label: '⭐ Talent' },
  { value: 'welfare', label: '🤝 Welfare' },
  { value: 'interview', label: '🎙️ Interview' },
];

export default function Stories() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const type = searchParams.get('type') || '';

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (type) params.type = type;
    if (search) params.search = search;
    storiesApi.list(params).then(r => {
      setStories(r.data.results || r.data);
      setLoading(false);
    });
  }, [type, search]);

  const setType = t => {
    const p = new URLSearchParams(searchParams);
    if (t) p.set('type', t); else p.delete('type');
    setSearchParams(p);
  };

  return (
    <div style={{ padding: '2.5rem 0' }}>
      <div className="container">
        <SEO
          title={type ? `${TYPES.find(t => t.value === type)?.label || 'Stories'} — African Football` : 'All Stories'}
          description="Browse personal stories, investigations, talent spotlights, welfare advocacy and interviews from across African football."
          url={`${SITE_URL}/stories${type ? `?type=${type}` : ''}`}
          jsonLd={{
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: 'Pitch & Pen — Stories',
            description: 'African football stories by players, journalists, scouts and fans.',
            url: `${SITE_URL}/stories`,
          }}
        />
        {/* Header */}
        <div style={{ marginBottom: '2rem' }}>
          <h1 style={{ fontSize: '2rem', marginBottom: '0.35rem' }}>Stories</h1>
          <p style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-sans)', fontSize: '0.95rem' }}>
            Voices from across African football.
          </p>
        </div>

        {/* Filters */}
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '2rem', alignItems: 'center' }}>
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search stories…"
            style={{
              flex: '1 1 220px', padding: '0.6rem 1rem',
              border: '1.5px solid var(--border)', borderRadius: 'var(--radius-sm)',
              fontFamily: 'var(--font-sans)', fontSize: '0.875rem', outline: 'none',
              transition: 'border-color 0.15s',
            }}
            onFocus={e => e.target.style.borderColor = 'var(--green)'}
            onBlur={e => e.target.style.borderColor = 'var(--border)'}
          />
          <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
            {TYPES.map(t => (
              <button key={t.value} onClick={() => setType(t.value)}
                style={{
                  padding: '0.45rem 1rem', borderRadius: 20, cursor: 'pointer',
                  fontFamily: 'var(--font-sans)', fontSize: '0.82rem', fontWeight: 500,
                  border: '1.5px solid', transition: 'all 0.12s',
                  background: type === t.value ? 'var(--green)' : 'white',
                  color: type === t.value ? 'white' : 'var(--text-muted)',
                  borderColor: type === t.value ? 'var(--green)' : 'var(--border)',
                }}>
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Results */}
        {loading ? (
          <div className="loading-text">Loading stories…</div>
        ) : stories.length === 0 ? (
          <div className="empty-state">
            <h3>No stories found</h3>
            <p>Try a different filter or search term.</p>
          </div>
        ) : (
          <>
            <div style={{ fontFamily: 'var(--font-sans)', fontSize: '0.82rem', color: 'var(--text-light)', marginBottom: '1rem' }}>
              {stories.length} {stories.length === 1 ? 'story' : 'stories'}
            </div>
            <div className="stories-grid">
              {stories.map(s => <StoryCard key={s.id} story={s} />)}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
